import type { JsonObject, JsonValue } from '@et/shared';
import { useT } from '../lib/i18n';

/**
 * Renders a form from the JSON Schema the server derives from each strategy's zod
 * paramSchema (API_DOCS.md — Entry Strategies). Supports what those schemas use: nested
 * objects (e.g. `gates`), booleans, integers/numbers, enums and decimal strings.
 * Validation stays on the server — its field-level errors come back as toasts.
 */

interface Schema {
  type?: string | string[];
  properties?: Record<string, Schema>;
  enum?: JsonValue[];
  default?: JsonValue;
  description?: string;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  anyOf?: Schema[];
}

const LABELS: Record<string, string> = {
  rvol: 'RVOL — is the market reacting?',
  priced_in: 'Priced-in — are we too late?',
  btc_regime: 'BTC regime — is the backdrop OK?',
  spread: 'Spread — is entry affordable?',
};

/** "minCompositeScore" → "Min composite score" — also the English i18n key for the label. */
export function humanize(key: string): string {
  const words = key.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return LABELS[key] ?? words.replace(/^./, (c) => c.toUpperCase());
}

/** A scalar JSON value as input text; objects/arrays never reach a text input. */
function text(v: JsonValue | undefined): string {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';
}

function typeOf(s: Schema): string {
  if (s.anyOf) return s.anyOf.map(typeOf).find((t) => t !== 'null') ?? 'string';
  return Array.isArray(s.type) ? (s.type.find((t) => t !== 'null') ?? 'string') : (s.type ?? 'string');
}

/** "300000" ms → "= 5 min": durations are shown in a unit a person reads. */
function durationHint(name: string, v: JsonValue | null, tr: (s: string, vars?: Record<string, string | number>) => string): string | null {
  if (typeof v !== 'number') return null;
  const ms = name.endsWith('Ms') ? v : name.endsWith('Sec') ? v * 1000 : name.endsWith('Min') ? v * 60_000 : null;
  if (ms === null) return null;
  if (ms < 60_000) return tr('= {n} seconds', { n: Math.round(ms / 100) / 10 });
  if (ms < 3_600_000) return tr('= {n} minutes', { n: Math.round(ms / 6_000) / 10 });
  return tr('= {n} hours', { n: Math.round(ms / 360_000) / 10 });
}

interface FieldOptions {
  readonly help?: Readonly<Record<string, string>>;
}

function Field({ name, schema, value, onChange, opts }: { name: string; schema: Schema; value: JsonValue | undefined; onChange: (v: JsonValue) => void; opts: FieldOptions }): JSX.Element {
  const t = typeOf(schema);
  const tr = useT();
  const current = value ?? schema.default ?? null;
  const label = tr(humanize(name));
  const helpText = opts.help?.[name];
  const help = helpText ? <span className="hint">{tr(helpText)}</span> : null;

  if (t === 'object' && schema.properties) {
    const obj = (current && typeof current === 'object' && !Array.isArray(current) ? current : {});
    return (
      <fieldset style={{ gridColumn: '1 / -1' }}>
        <legend>{label}</legend>
        {helpText && <div className="hint" style={{ marginBottom: 8 }}>{tr(helpText)}</div>}
        <ObjectFields schema={schema} value={obj} onChange={onChange} opts={opts} />
      </fieldset>
    );
  }
  if (t === 'boolean') {
    return (
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={current === true} onChange={(e) => { onChange(e.target.checked); }} />
        {label}
        {help}
      </label>
    );
  }
  if (schema.enum) {
    return (
      <label className="field">
        {label}
        <select value={text(current)} onChange={(e) => { onChange(e.target.value); }}>
          {schema.enum.map((o) => <option key={text(o)} value={text(o)}>{text(o)}</option>)}
        </select>
        {help}
      </label>
    );
  }
  if (t === 'integer' || t === 'number') {
    return (
      <label className="field">
        {label}
        <input
          type="number"
          value={text(current)}
          min={schema.minimum}
          max={schema.maximum}
          step={t === 'integer' ? 1 : 'any'}
          onChange={(e) => { onChange(e.target.value === '' ? null : Number(e.target.value)); }}
        />
        {help}
        {durationHint(name, current, tr) && <span className="hint">{durationHint(name, current, tr)}</span>}
        {schema.minimum !== undefined && schema.maximum !== undefined && <span className="hint">{tr('allowed: {min} – {max}', { min: schema.minimum, max: schema.maximum })}</span>}
      </label>
    );
  }
  return (
    <label className="field">
      {label}
      <input className="mono" value={text(current)} inputMode={schema.pattern ? 'decimal' : 'text'} onChange={(e) => { onChange(e.target.value); }} />
      {help}
    </label>
  );
}

function ObjectFields({ schema, value, onChange, opts, keys }: { schema: Schema; value: JsonObject; onChange: (v: JsonObject) => void; opts: FieldOptions; keys?: readonly string[] }): JSX.Element {
  const entries = Object.entries(schema.properties ?? {}).filter(([k]) => keys === undefined || keys.includes(k));
  return (
    <div className="form-grid">
      {entries.map(([key, s]) => (
        <Field key={key} name={key} schema={s} value={value[key]} onChange={(v) => { onChange({ ...value, [key]: v }); }} opts={opts} />
      ))}
    </div>
  );
}

/**
 * `only` / `except` pick top-level fields, so a page can show the main settings up front and
 * tuck the rest under "Advanced settings" — both halves edit the same params object.
 */
export function SchemaForm(props: {
  schema: JsonObject;
  value: JsonObject;
  onChange: (v: JsonObject) => void;
  help?: Readonly<Record<string, string>>;
  only?: readonly string[];
  except?: readonly string[];
}): JSX.Element {
  const schema = props.schema as Schema;
  const all = Object.keys(schema.properties ?? {});
  const keys = props.only ?? (props.except ? all.filter((k) => !props.except?.includes(k)) : undefined);
  return <ObjectFields schema={schema} value={props.value} onChange={props.onChange} opts={props.help ? { help: props.help } : {}} {...(keys ? { keys } : {})} />;
}
